import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import { getDisplayableActions } from '~/components/AiChat/SideBarDialog/Actions/actionsUtils';
import {
  generateAdjusterAssignmentActionData,
  generateCommunicationActionData,
  generateOpenExposureActionData,
  generateSetReserveActionData,
} from '~/components/AiChat/SideBarDialog/Actions/demoActions';
import { useActionsFeatureFlags } from '~/components/AiChat/SideBarDialog/Actions/useActionsFeatureFlags';
import type { AiAction, ReserveActionData } from '~/components/AiChat/types';
import { AI_ACTION_TYPES } from '~/components/AiChat/types';
import { useClaim } from '~/components/ClaimContainer';

export interface IActionsDataContextData {
  actions: AiAction[];
  filteredActions: AiAction[];
  isLoadingFirstTime: boolean;
  isLoading: boolean;
  isError: boolean;
  reloadData: () => Promise<void>;
  isDisabled: boolean;
}

const ActionsDataContext = React.createContext<IActionsDataContextData>({
  actions: [],
  filteredActions: [],
  isLoadingFirstTime: true,
  isLoading: true,
  isError: false,
  reloadData: Promise.resolve,
  isDisabled: false,
});

const DISABLED_ACTIONS_DATA: IActionsDataContextData = {
  actions: [],
  filteredActions: [],
  isLoadingFirstTime: false,
  isLoading: false,
  isError: false,
  reloadData: () => Promise.resolve(),
  isDisabled: true,
};

export const ActionsDataProvider: React.FC<React.PropsWithChildren<{ isDisabled: boolean }>> = ({
  isDisabled,
  children,
}) => {
  const [actions, setActions] = useState<AiAction[]>([]);
  const [isLoadingFirstTime, setIsLoadingFirstTime] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [demoActions, setDemoActions] = useState<AiAction[]>([]);

  const { claim } = useClaim({ claimRequired: true });
  const { actionFlagEnabledMapping, hasDemoActions } = useActionsFeatureFlags();
  const [shouldBuildDemoActions, setShouldBuildDemoActions] = useState(hasDemoActions);

  const generateStaticDemoActions = useCallback(
    (handlingAdjuster: string, reserves: ReserveActionData[], claimCreationDate: Date): AiAction[] => {
      const allowedDemoActions: AiAction[] = [];
      if (!actionFlagEnabledMapping) return allowedDemoActions;

      if (actionFlagEnabledMapping[AI_ACTION_TYPES.ADJUSTER_ASSIGNMENT] && handlingAdjuster) {
        allowedDemoActions.push(generateAdjusterAssignmentActionData(claimCreationDate, handlingAdjuster));
      }

      if (actionFlagEnabledMapping[AI_ACTION_TYPES.OPEN_EXPOSURE]) {
        claim.exposures?.forEach((exposure) => {
          allowedDemoActions.push(generateOpenExposureActionData(claimCreationDate, exposure));
        });
      }

      if (actionFlagEnabledMapping[AI_ACTION_TYPES.SET_RESERVE]) {
        reserves?.forEach((reserve: ReserveActionData) => {
          allowedDemoActions.push(generateSetReserveActionData(claimCreationDate, reserve));
        });
      }

      if (actionFlagEnabledMapping[AI_ACTION_TYPES.COMMUNICATE]) {
        allowedDemoActions.push(generateCommunicationActionData(claimCreationDate));
      }

      return allowedDemoActions;
    },
    [actionFlagEnabledMapping, claim.exposures]
  );

  const addDemoActionsIfNeeded = useCallback(
    (handlingAdjuster: string, reserves: ReserveActionData[]): AiAction[] | undefined => {
      if (!shouldBuildDemoActions) return undefined;

      const freshDemoActions = generateStaticDemoActions(
        handlingAdjuster,
        reserves,
        claim.creation_date ? new Date(claim.creation_date) : new Date()
      );
      setDemoActions(freshDemoActions);
      setShouldBuildDemoActions(false);
      return freshDemoActions;
    },
    [shouldBuildDemoActions, generateStaticDemoActions, claim.creation_date]
  );

  // The Clive Insights tab this provider lives in is itself gated on the `clive_insights_claim_tab`
  // Clive configuration toggle (`useClaimSideBarTabs/utils.ts`), and the tab is neither preloaded
  // nor persisted, so nothing mounts this provider while that is off.
  const fetchActions = useCallback(async () => {
    try {
      setIsLoading(true);
      const url = `/api/v1/claims/${claim.id}/actions${shouldBuildDemoActions ? '?additional_data=true' : ''}`;
      const res = await axios.get(url);
      const freshDemoActions = addDemoActionsIfNeeded(res.data?.first_handling_adjuster_name, res.data?.reserves);

      const fetchedActions = [...(freshDemoActions || demoActions || []), ...(res.data?.actions || [])];
      setActions(fetchedActions);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
      if (isLoadingFirstTime) {
        setIsLoadingFirstTime(false);
      }
    }
  }, [claim.id, shouldBuildDemoActions, addDemoActionsIfNeeded, demoActions, isLoadingFirstTime]);

  // `isDisabled` can flip while mounted: the sidebar derives it from the default tabs until the
  // organization's own sidebar configuration arrives.
  useEffect(() => {
    if (!isDisabled) fetchActions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDisabled]);

  const filteredActions = useMemo(
    () => getDisplayableActions(actions, actionFlagEnabledMapping),
    [actions, actionFlagEnabledMapping]
  );

  return (
    <ActionsDataContext.Provider
      value={
        isDisabled
          ? DISABLED_ACTIONS_DATA
          : { actions, filteredActions, isLoadingFirstTime, isLoading, isError, reloadData: fetchActions, isDisabled }
      }
    >
      {children}
    </ActionsDataContext.Provider>
  );
};

export const useActionsData = (): IActionsDataContextData => {
  return React.useContext(ActionsDataContext);
};
